/**
 * Funnel / Analytics API Service (TASK-238)
 *
 * API client for funnel analytics, contacts, stage configuration,
 * event→stage mappings and per-stage operator prompts.
 */
import apiClient from './client';
import type {
  FunnelAnalytics,
  FunnelAnalyticsFilters,
  FunnelStageStatsResponse,
  FunnelStatsChannel,
  FunnelStatsPeriod,
  FunnelContact,
  FunnelStageConfig,
  UpdateFunnelStagePayload,
  EventStageMapping,
  UpdateEventStageMappingPayload,
  NextStepType,
  CreateNextStepTypePayload,
  UpdateNextStepTypePayload,
  StagePromptsResponse,
} from '@/types/funnel';

export const funnelApi = {
  /**
   * Funnel analytics with period + channel/operator/outcome filters.
   * GET /api/v1/analytics/funnel/
   */
  getAnalytics: async (
    filters?: FunnelAnalyticsFilters
  ): Promise<FunnelAnalytics> => {
    const response = await apiClient.get<FunnelAnalytics>(
      '/api/v1/analytics/funnel/',
      { params: filters }
    );
    return response.data;
  },

  /**
   * KAN-737: per-stage cohort counts of the dashboard funnel block.
   * GET /api/v1/analytics/funnel/stages/?period=1d|7d|30d|all&channel=outreach|calls|all
   */
  getStageStats: async (
    period: FunnelStatsPeriod,
    channel: FunnelStatsChannel
  ): Promise<FunnelStageStatsResponse> => {
    const response = await apiClient.get<FunnelStageStatsResponse>(
      '/api/v1/analytics/funnel/stages/',
      { params: { period, channel } }
    );
    return response.data;
  },

  /**
   * List of contacts (touchpoints).
   * GET /api/v1/contacts/
   *
   * Resilient to both DRF response shapes: a bare array, or a paginated
   * object `{ count, next, previous, results: [...] }`.
   */
  listContacts: async (): Promise<FunnelContact[]> => {
    const response = await apiClient.get('/api/v1/contacts/');
    const d = response.data;
    return Array.isArray(d) ? d : (d?.results ?? []);
  },

  /**
   * List contacts (touchpoints), optionally scoped to a single client.
   * GET /api/v1/contacts/?client={clientId}
   *
   * Resilient to both DRF response shapes (array or paginated `{ results }`).
   */
  getContacts: async (clientId?: string): Promise<FunnelContact[]> => {
    const response = await apiClient.get('/api/v1/contacts/', {
      params: clientId ? { client: clientId } : undefined,
    });
    const d = response.data;
    return Array.isArray(d) ? d : (d?.results ?? []);
  },

  /**
   * Download a contact call recording as a binary blob (TASK-329).
   * GET /api/v1/contacts/{contactId}/recording/
   *
   * The endpoint requires auth headers, so a plain `<audio src>` cannot be
   * used. Callers turn the returned blob into an object URL
   * (`URL.createObjectURL`) and must revoke it when done.
   */
  getContactRecordingBlob: async (contactId: string): Promise<Blob> => {
    const response = await apiClient.get(
      `/api/v1/contacts/${contactId}/recording/`,
      { responseType: 'blob' }
    );
    return response.data as Blob;
  },

  /**
   * List configurable funnel stages.
   * GET /api/v1/funnel/stages/
   *
   * Resilient to both DRF response shapes (array or paginated `{ results }`).
   */
  listStages: async (): Promise<FunnelStageConfig[]> => {
    const response = await apiClient.get('/api/v1/funnel/stages/');
    const d = response.data;
    return Array.isArray(d) ? d : (d?.results ?? []);
  },

  /**
   * Get configurable funnel stages (alias used by per-client widgets).
   * GET /api/v1/funnel/stages/
   *
   * Resilient to both DRF response shapes: a bare array, or a paginated
   * object `{ count, next, previous, results: [...] }`. With global
   * pagination enabled this endpoint returns the object form, so unwrapping
   * `results` here prevents `.filter` from throwing on the consumer side.
   */
  getStages: async (): Promise<FunnelStageConfig[]> => {
    const response = await apiClient.get('/api/v1/funnel/stages/');
    const d = response.data;
    return Array.isArray(d) ? d : (d?.results ?? []);
  },

  /**
   * Update a funnel stage (label / order / is_active / overrides).
   * PATCH /api/v1/funnel/stages/{id}/
   */
  updateStage: async (
    id: string,
    payload: UpdateFunnelStagePayload
  ): Promise<FunnelStageConfig> => {
    const response = await apiClient.patch<FunnelStageConfig>(
      `/api/v1/funnel/stages/${id}/`,
      payload
    );
    return response.data;
  },

  /**
   * List event → stage mappings.
   * GET /api/v1/funnel/mappings/
   */
  listMappings: async (): Promise<EventStageMapping[]> => {
    const response = await apiClient.get<EventStageMapping[]>(
      '/api/v1/funnel/mappings/'
    );
    return response.data;
  },

  /**
   * Update an event → stage mapping target.
   * PATCH /api/v1/funnel/mappings/{id}/
   */
  updateMapping: async (
    id: string,
    payload: UpdateEventStageMappingPayload
  ): Promise<EventStageMapping> => {
    const response = await apiClient.patch<EventStageMapping>(
      `/api/v1/funnel/mappings/${id}/`,
      payload
    );
    return response.data;
  },

  /**
   * Delete an event → stage mapping.
   * DELETE /api/v1/funnel/mappings/{id}/
   */
  deleteMapping: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/funnel/mappings/${id}/`);
  },

  /**
   * List configurable next-step (follow-up) types (TASK-301).
   * GET /api/v1/funnel/next-step-types/
   *
   * Resilient to both DRF response shapes (array or paginated `{ results }`).
   */
  listNextStepTypes: async (): Promise<NextStepType[]> => {
    const response = await apiClient.get('/api/v1/funnel/next-step-types/');
    const d = response.data;
    return Array.isArray(d) ? d : (d?.results ?? []);
  },

  /**
   * Create a next-step type.
   * POST /api/v1/funnel/next-step-types/
   */
  createNextStepType: async (
    payload: CreateNextStepTypePayload
  ): Promise<NextStepType> => {
    const response = await apiClient.post<NextStepType>(
      '/api/v1/funnel/next-step-types/',
      payload
    );
    return response.data;
  },

  /**
   * Update a next-step type (label / event / active / order).
   * `codename` is read-only on the server and must not be sent.
   * PATCH /api/v1/funnel/next-step-types/{id}/
   */
  updateNextStepType: async (
    id: string,
    payload: UpdateNextStepTypePayload
  ): Promise<NextStepType> => {
    const response = await apiClient.patch<NextStepType>(
      `/api/v1/funnel/next-step-types/${id}/`,
      payload
    );
    return response.data;
  },

  /**
   * Delete a next-step type.
   * DELETE /api/v1/funnel/next-step-types/{id}/
   */
  deleteNextStepType: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/funnel/next-step-types/${id}/`);
  },

  /**
   * Get per-stage operator prompts for a user.
   * GET /api/v1/users/{id}/stage-prompts/
   */
  getStagePrompts: async (userId: string): Promise<StagePromptsResponse> => {
    const response = await apiClient.get<StagePromptsResponse>(
      `/api/v1/users/${userId}/stage-prompts/`
    );
    return response.data;
  },

  /**
   * Save a single per-stage operator prompt.
   * PUT /api/v1/users/{id}/stage-prompts/{stage_codename}/
   */
  updateStagePrompt: async (
    userId: string,
    stageCodename: string,
    prompt: string
  ): Promise<void> => {
    await apiClient.put(
      `/api/v1/users/${userId}/stage-prompts/${stageCodename}/`,
      { prompt }
    );
  },
};

export default funnelApi;
